<!--
  Product Detail Modal — versione modal-embedded.
  Redesigned 2026-06-15 (v2.9.0 polish, stesso pattern visivo di
  app-product-detail B.3 v2.7.0).

  Two-pane modal:
    LEFT  — product info (codice, descrizione, categorie, prezzo,
            peso, stock, impegnato, disponibile + sottoprodotti).
    RIGHT — warehouse positions list + add/delete actions.

  Apertura: @Input opened gate (set via `closeServ.prodDetOn()` o
  `closeServ.onStoricoProd()`). Chiusura: backdrop click o X →
  `close()` (chiama prodDetOff() + offStoricoProd()).

  Z-index 2100 (apre sopra storico-ordini-dettagli H.2 a 2000).

  Sub-modal preserved:
    - app-product-position (add position via openSrv.onPos)
-->

@if (opened || storicoOn) {
  <div class="modal-backdrop" (click)="close()">
    <aside class="modal modal--xl" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">

      <!-- ─── Header ─────────────────────────────────────────────── -->
      <header class="modal__header">
        <h2 class="modal__title">
          📦 Dettaglio prodotto
          @if (product?.pr_code) {
            <span class="modal__title-tag">{{ product.pr_code }}</span>
          }
        </h2>
        <button type="button" class="modal-close" (click)="close()" aria-label="Chiudi">×</button>
      </header>

      <!-- ─── Body — 2 panes ─────────────────────────────────────── -->
      <div class="modal__body">

        @if (loading) {
          <div class="loading-card">
            <div class="loading-card__spinner"></div>
            <p>Caricamento prodotto…</p>
          </div>
        } @else if (!product || !product.pr_code) {
          <div class="loading-card">
            <p>Prodotto non trovato.</p>
          </div>
        } @else {

          <div class="detail-grid">

            <!-- ─── LEFT: product info ───────────────────────── -->
            <section class="product-info" aria-label="Informazioni prodotto">

              <header class="info-card__header">
                <h3 class="info-card__title">Informazioni</h3>
              </header>

              <dl class="info-grid">
                <div class="info-field">
                  <dt class="info-field__label">Codice</dt>
                  <dd class="info-field__value info-field__value--strong">
                    {{ product.pr_code || '—' }}
                  </dd>
                </div>

                <div class="info-field info-field--full">
                  <dt class="info-field__label">Descrizione</dt>
                  <dd class="info-field__value">
                    {{ product.pr_description || '—' }}
                  </dd>
                </div>

                <div class="info-field">
                  <dt class="info-field__label">Famiglia</dt>
                  <dd class="info-field__value">
                    {{ product.family_desc || '—' }}
                  </dd>
                </div>

                <div class="info-field">
                  <dt class="info-field__label">Gruppo</dt>
                  <dd class="info-field__value">
                    {{ product.group_desc || '—' }}
                  </dd>
                </div>

                <div class="info-field">
                  <dt class="info-field__label">Prezzo</dt>
                  <dd class="info-field__value info-field__value--num">
                    @if (isValidNumber(product.pr_price)) {
                      {{ product.pr_price | currency:'EUR':'symbol':'1.2-2' }}
                    } @else { — }
                  </dd>
                </div>

                <div class="info-field">
                  <dt class="info-field__label">Peso</dt>
                  <dd class="info-field__value info-field__value--num">
                    @if (isValidNumber(product.pr_weight)) {
                      {{ product.pr_weight | number:'1.2-2' }} kg
                    } @else { — }
                  </dd>
                </div>
              </dl>

              <!-- ─── Stock metrics ─── -->
              <div class="stock-metrics">
                <div class="stock-metric stock-metric--neutral">
                  <span class="stock-metric__label">Stock</span>
                  <span class="stock-metric__value">
                    @if (isValidNumber(product.pr_stock)) {
                      {{ product.pr_stock | number:'1.0-0' }}
                    } @else { 0 }
                  </span>
                </div>

                <div class="stock-metric stock-metric--warn">
                  <span class="stock-metric__label">Impegnato</span>
                  <span class="stock-metric__value">
                    {{ product.committed ?? 0 }}
                  </span>
                </div>

                <div
                  class="stock-metric"
                  [class.stock-metric--ok]="(+product.available || 0) > 0"
                  [class.stock-metric--danger]="(+product.available || 0) <= 0"
                >
                  <span class="stock-metric__label">Disponibile</span>
                  <span class="stock-metric__value">
                    {{ product.available ?? 0 }}
                  </span>
                </div>
              </div>

              <!-- ─── Sub-products (if any) ─── -->
              @if (product?.sub_products?.length) {
                <header class="info-card__header info-card__header--secondary">
                  <h3 class="info-card__title">Sottoprodotti</h3>
                  <span class="info-card__count">{{ product.sub_products.length }}</span>
                </header>

                <ul class="subproducts-list" role="list">
                  @for (sp of product.sub_products; track sp.osp_code + '-' + ($index)) {
                    <li class="subproduct-row">
                      <span class="subproduct-row__code">{{ sp.osp_code }}</span>
                      <span class="subproduct-row__desc">{{ sp.osp_description }}</span>
                    </li>
                  }
                </ul>
              }

            </section>

            <!-- ─── RIGHT: warehouse positions ──────────────── -->
            <section class="positions-pane" aria-label="Posizioni in magazzino">

              <header class="positions-pane__header">
                <h3 class="info-card__title">📍 Posizioni in magazzino</h3>
                <button
                  type="button"
                  class="btn btn--primary btn--sm"
                  (click)="openAddPosition()"
                >+ Aggiungi</button>
              </header>

              @if (!pr_position?.length) {
                <p class="positions-empty">
                  Nessuna posizione registrata per questo prodotto.
                </p>
              } @else {
                <ul class="positions-list" role="list">
                  @for (position of pr_position; track position.product_position_id + '-' + ($index)) {
                    <li class="position-card">
                      <div class="position-card__coords">
                        <span class="position-card__chip">
                          <span class="position-card__chip-label">Corsia</span>
                          <span class="position-card__chip-value">
                            {{ position.pr_lane || '—' }}
                          </span>
                        </span>
                        <span class="position-card__chip">
                          <span class="position-card__chip-label">Scaffale</span>
                          <span class="position-card__chip-value">
                            {{ position.pr_shelf || '—' }}
                          </span>
                        </span>
                        <span class="position-card__chip">
                          <span class="position-card__chip-label">Posizione</span>
                          <span class="position-card__chip-value">
                            {{ position.pr_position || '—' }}
                          </span>
                        </span>
                        <span class="position-card__chip position-card__chip--qty">
                          <span class="position-card__chip-label">Q.tà</span>
                          <span class="position-card__chip-value">
                            {{ position.pr_qty ?? 0 }}
                          </span>
                        </span>
                      </div>

                      @if (position?.pr_b64_position_image) {
                        <div class="position-card__image-wrap">
                          <img
                            class="position-card__image"
                            [src]="'data:image/png;base64,' + position.pr_b64_position_image"
                            alt="Immagine posizione"
                          />
                        </div>
                      }

                      <div class="position-card__actions">
                        <button
                          type="button"
                          class="btn btn--danger btn--sm"
                          (click)="deleteProductPosition(position.product_position_id)"
                          title="Elimina posizione"
                        >🗑 Elimina</button>
                      </div>
                    </li>
                  }
                </ul>
              }

            </section>

          </div>
        }

      </div>

    </aside>
  </div>
}

<!-- ─── Add-position sub-modal ────────────────────────────────── -->
@if (openSrv.isPosOn) {
  <app-product-position
    [openPhoto]="openSrv.isPosOn"
    [product]="product"
    (modalClosed)="triggerRefresh()"
  ></app-product-position>
}
